"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { BsPlayCircle } from "react-icons/bs";
import Image from "next/image";
import { Loader } from "../loader";
import { generateIcon } from "utils";
import type { VideoItemType } from "./types";
import MediaGalleryPictograms from "./MediaGalleryPictograms";
import MediaDownloadButton from "./MediaDownloadButton";

interface IVideoGalleryItemProps {
  item: VideoItemType;
  index: number;
  handleOpen: (e: React.MouseEvent<Element | any>) => void;
  videoRef: any;
  recentlySeenItems: string[];
  showQrStatus: boolean;
  timeZone?: string;
}

export default function VideoGalleryItem({
  item,
  index,
  handleOpen,
  videoRef,
  recentlySeenItems = [],
  showQrStatus,
  timeZone,
}: IVideoGalleryItemProps) {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<string>(
    item.thumbnail || "./assets/video-poster.jpg"
  );
  const isProcessed = useMemo(() => item.uploaded === item.fileLength, [item]);
  const isNew = !item.seenOnUtc && !recentlySeenItems.includes(item.id);
  const t = useTranslations();

  const handleOpenButton = () => (
    <button
      type="button"
      className="absolute z-20 w-full inset-0 flex justify-end items-start group-hover:opacity-100 text-right opacity-80"
      onClick={(e) => handleOpen(e)}
      ref={videoRef}
    >
      <span className="flex items-center p-2">
        <span className="text-white text-xs">
          {item.duration.slice(3, 8)} &nbsp;
        </span>

        {isLoading ? (
          <span className="w-4 h-4">
            <Loader colorClassName="text-white" />
          </span>
        ) : (
          <span>{generateIcon(BsPlayCircle, "w-5 h-5 text-white")}</span>
        )}
      </span>
    </button>
  );

  const getPercentage = () =>
    `${((item.uploaded / item.fileLength) * 100).toFixed(2)}%`;

  if (!isProcessed) {
    return (
      <div className="relative">
        <Image
          src={thumbnail}
          width={item.width}
          height={item.height}
          className={`object-contain aspect-video ${
            isProcessed ? "cursor-pointer" : ""
          }`}
          alt={item.id}
        />

        <div className="absolute inset-0 flex justify-center items-center text-white text-[11px] pointer-events-none">
          <p className="flex items-center bg-black/80 text-white py-[3px] px-2 rounded">
            {t("Gallery.notUplodadedYet")} {getPercentage()}
          </p>
        </div>

        <div className="absolute z-20 w-full inset-0 flex justify-end items-start group-hover:opacity-100 text-right opacity-80">
          <span className="flex items-center p-2">
            {generateIcon(BsPlayCircle, "w-5 h-5 text-white")}
          </span>
        </div>
        {handleOpenButton()}
      </div>
    );
  }

  return (
    <div
      className="self-center relative group aspect-video"
      onMouseEnter={() => {
        setIsHovered(true);
        if (!isLoaded) {
          setIsLoading(true);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsLoading(false);
      }}
    >
      <Image
        src={thumbnail}
        width={item.width}
        height={item.height}
        className={`object-contain aspect-video select-none ${
          isProcessed ? "cursor-pointer" : ""
        }`}
        alt={item.id}
        loading="lazy"
        onError={() => setThumbnail("/assets/video-poster.jpg")}
      />

      {isHovered ? (
        <div className="absolute inset-0">
          <video
            className="w-full aspect-video"
            id={`videoItem_${index}`}
            muted
            playsInline
            autoPlay
            preload="auto"
            loop
            onLoadedData={() => {
              setIsLoaded(true);
              setIsLoading(false);
            }}
          >
            <source src={item.url} type="video/mp4" />
          </video>
        </div>
      ) : null}

      <MediaGalleryPictograms
        userConsent={item?.userConsent}
        isNew={isNew}
        showQrStatus={showQrStatus}
        date={item.date}
        timeZone={timeZone}
      />
      {handleOpenButton()}
      {/* Below the play icon so the two do not overlap. */}
      <MediaDownloadButton item={item} className="top-9 right-1" />
    </div>
  );
}
